// The reorder drag contract, component-free (react-refresh requires a module that exports a component
// to export ONLY components — same split as features/assets/photoSwap.ts).
import { useState, type DragEvent } from "react";

/** `application/x-cntrl-<kind>` — a private DataTransfer type so one list's drag can't land in another,
 *  and an OS file drop can never be mistaken for a row drag. */
export const dragType = (kind: string) => `application/x-cntrl-${kind}`;

/**
 * Drop props for the ROW that a {@link DragHandle} drags onto. Returns spreadable handlers plus the
 * `data-drag-over` hook the stylesheet keys its highlight off.
 */
export function useDropTarget({
  index,
  kind,
  onMove,
}: {
  index: number;
  kind: string;
  onMove: (from: number, to: number) => void;
}) {
  const [isOver, setIsOver] = useState(false);
  const type = dragType(kind);
  return {
    "data-drag-over": isOver || undefined,
    onDragOver: (e: DragEvent<HTMLElement>) => {
      if (!e.dataTransfer.types.includes(type)) return; // not ours — let it pass through
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setIsOver(true);
    },
    onDragLeave: () => setIsOver(false),
    onDrop: (e: DragEvent<HTMLElement>) => {
      setIsOver(false);
      const raw = e.dataTransfer.getData(type);
      if (!raw) return;
      e.preventDefault();
      const from = Number(raw);
      if (Number.isInteger(from) && from !== index) onMove(from, index);
    },
  };
}
